import React, { useEffect, useState } from 'react';
import styles from './NewPatientWizard.module.css';
import { supabase } from '../auth/supabase';
import { apiFetch } from '../api/client';
import ToothChart from './ToothChart';

export default function NewPatientWizard({ onClose }) {
  const [step, setStep] = useState(1); // 1: patient info, 2: plan items
  const [lookups, setLookups] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [patient, setPatient] = useState({
    mrn: '', full_name: '', age: '', sex: 'M', chief_complaint: '', medical_hx: '', dental_hx: ''
  });
  const [items, setItems] = useState([]);

  const getToken = async () => (await supabase.auth.getSession()).data.session?.access_token;

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch('/lookups', {}, getToken);
        setLookups(data);
      } catch (e) {
        setError(e.message || 'Failed to load lookups');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addRow = (tooth) => setItems((prev) => ([...prev, { tooth: tooth ? String(tooth) : '', diagnosis_id: '', procedure_id: '', status_id: '', notes: '', sort_order: prev.length }]));
  const updateRow = (i, f, v) => setItems((prev) => prev.map((r, idx) => idx === i ? { ...r, [f]: v } : r));
  const removeRow = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i).map((r, idx) => ({ ...r, sort_order: idx })));

  const canNext = step === 1 ? patient.full_name.trim().length > 0 : items.some(r => r.tooth && (r.diagnosis_id || r.procedure_id));

  const saveAll = async () => {
    setSaving(true); setError('');
    try {
      // Create patient
      const payload = {
        mrn: patient.mrn || null,
        full_name: patient.full_name,
        age: patient.age ? parseInt(patient.age, 10) : null,
        sex: patient.sex || null,
        chief_complaint: patient.chief_complaint || null,
        medical_hx: patient.medical_hx || null,
        dental_hx: patient.dental_hx || null,
      };
      const p = await apiFetch('/patients', { method: 'POST', body: payload }, getToken);
      // Create plan
      const plan = await apiFetch(`/patients/${p.id}/plans`, { method: 'POST', body: { title: 'Active Plan' } }, getToken);
      // Create items
      for (const [i, row] of items.entries()) {
        if (!row.tooth || (!row.diagnosis_id && !row.procedure_id)) continue;
        await apiFetch(`/plans/${plan.id}/items`, { method: 'POST', body: {
          tooth: parseInt(row.tooth, 10),
          diagnosis_id: row.diagnosis_id ? parseInt(row.diagnosis_id, 10) : null,
          procedure_id: row.procedure_id ? parseInt(row.procedure_id, 10) : null,
          status_id: row.status_id ? parseInt(row.status_id, 10) : null,
          notes: row.notes || null,
          sort_order: i,
        }}, getToken);
      }
      // Navigate to patient plan tab
      window.location.href = `/patients/${p.id}?tab=plan`;
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={() => !saving && onClose?.()}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <div className={styles.title}>Add patient to workflow</div>
          <button className={styles.close} onClick={() => !saving && onClose?.()} aria-label="Close">×</button>
        </div>
        <div className={styles.stepper}>
          <div className={step >= 1 ? styles.stepActive : styles.step}>STEP 1<div className={styles.stepLabel}>Basic Information</div></div>
          <div className={styles.stepDivider}/>
          <div className={step >= 2 ? styles.stepActive : styles.step}>STEP 2<div className={styles.stepLabel}>Treatment Plan</div></div>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        {loading ? (
          <div className={styles.info}>Loading…</div>
        ) : step === 1 ? (
          <div className={styles.body}>
            <div className={styles.grid}>
              <label className={styles.label}><span>Full Name</span><input className={styles.input} value={patient.full_name} onChange={(e) => setPatient({ ...patient, full_name: e.target.value })} /></label>
              <label className={styles.label}><span>MRN</span><input className={styles.input} value={patient.mrn} onChange={(e) => setPatient({ ...patient, mrn: e.target.value })} /></label>
              <label className={styles.label}><span>Age</span><input className={styles.input} type="number" min="0" value={patient.age} onChange={(e) => setPatient({ ...patient, age: e.target.value })} /></label>
              <label className={styles.label}><span>Gender</span>
                <select className={styles.input} value={patient.sex} onChange={(e) => setPatient({ ...patient, sex: e.target.value })}>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                </select>
              </label>
            </div>
            <label className={styles.label}><span>Chief Complaint</span><textarea className={styles.textarea} rows={3} value={patient.chief_complaint} onChange={(e) => setPatient({ ...patient, chief_complaint: e.target.value })} /></label>
            <div className={styles.grid2}>
              <label className={styles.label}><span>Medical History</span><textarea className={styles.textarea} rows={3} value={patient.medical_hx} onChange={(e) => setPatient({ ...patient, medical_hx: e.target.value })} /></label>
              <label className={styles.label}><span>Dental History</span><textarea className={styles.textarea} rows={3} value={patient.dental_hx} onChange={(e) => setPatient({ ...patient, dental_hx: e.target.value })} /></label>
            </div>
          </div>
        ) : (
          <div className={styles.body}>
            <div className={styles.helper}>Click teeth to add rows. Add at least one tooth with Diagnosis and/or Proposed Tx.</div>
            <ToothChart selected={items.map(r => parseInt(r.tooth||0,10)).filter(Boolean)} onSelect={(n) => addRow(n)} />
            <div className={styles.rows}>
              {items.map((r, i) => (
                <div key={i} className={styles.row}>
                  <select className={styles.select} value={r.tooth} onChange={(e) => updateRow(i, 'tooth', e.target.value)}>
                    <option value="">Tooth</option>
                    {lookups?.teeth.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <select className={styles.select} value={r.diagnosis_id} onChange={async (e) => {
                    const val = e.target.value;
                    if (val === '__create_diagnosis__') {
                      const name = prompt('New diagnosis name');
                      if (name && name.trim()) {
                        try {
                          const created = await apiFetch('/lookups/diagnoses', { method: 'POST', body: { name } }, getToken);
                          setLookups((prev) => ({ ...prev, diagnoses: [...(prev?.diagnoses||[]), created] }));
                          updateRow(i, 'diagnosis_id', String(created.id));
                        } catch (err) { alert(err.message || 'Failed to create'); }
                      }
                      e.target.value = '';
                      return;
                    }
                    updateRow(i, 'diagnosis_id', val);
                  }}>
                    <option value="">Diagnosis</option>
                    {lookups?.diagnoses.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    <option value="__create_diagnosis__">Create…</option>
                  </select>
                  <select className={styles.select} value={r.procedure_id} onChange={async (e) => {
                    const val = e.target.value;
                    if (val === '__create_procedure__') {
                      const name = prompt('New procedure name');
                      if (name && name.trim()) {
                        try {
                          const created = await apiFetch('/lookups/procedures', { method: 'POST', body: { name } }, getToken);
                          setLookups((prev) => ({ ...prev, procedures: [...(prev?.procedures||[]), created] }));
                          updateRow(i, 'procedure_id', String(created.id));
                        } catch (err) { alert(err.message || 'Failed to create'); }
                      }
                      e.target.value = '';
                      return;
                    }
                    updateRow(i, 'procedure_id', val);
                  }}>
                    <option value="">Proposed Tx</option>
                    {lookups?.procedures.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    <option value="__create_procedure__">Create…</option>
                  </select>
                  <select className={styles.select} value={r.status_id} onChange={(e) => updateRow(i, 'status_id', e.target.value)}>
                    <option value="">Status</option>
                    {lookups?.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  <input className={styles.notes} placeholder="Notes" value={r.notes} onChange={(e) => updateRow(i, 'notes', e.target.value)} />
                  <button className={styles.rowDelete} onClick={() => removeRow(i)}>🗑</button>
                </div>
              ))}
              <button className={styles.addRow} onClick={() => addRow()}>+ Add Tooth</button>
            </div>
          </div>
        )}
        <div className={styles.footer}>
          {step === 1 ? (
            <button className={styles.btnGhost} onClick={() => onClose?.()}>Cancel</button>
          ) : (
            <button className={styles.btnGhost} onClick={() => setStep(1)} disabled={saving}>Previous</button>
          )}
          {step === 1 ? (
            <button className={styles.btnPrimary} onClick={() => setStep(2)} disabled={!canNext}>Next</button>
          ) : (
            <button className={styles.btnPrimary} onClick={saveAll} disabled={!canNext || saving}>{saving ? 'Saving…' : 'Create'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
